import React, { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  SearchIcon,
  PlusIcon,
  CloseIcon,
  ChevronDownIcon,
  ViewIcon,
  DownloadIcon,
  UsersIcon,
  ArchiveIcon,
  ArchiveRestoreIcon,
} from "../../Icons";
import Table from "../../components/UI/Table";
import StatusBadge, {
  STATUS_LABELS,
  PATIENT_STATUS_FILTER_ORDER,
} from "./components/StatusBadge";
import PatientArchiveConfirmModal from "./components/PatientArchiveConfirmModal";
import usePatients from "./usePatients";
import { resolveUserRoleName, normalizeRoleCode } from "../../constants";
import { canArchivePatient, ARCHIVED_PATIENT_LIST_ACTIONS } from "./patientPermissions";
import { displayDateUS } from "../../utils/dateUtils";
import { formatPhone } from "../Application/application.validation";
import { PATIENT_PAGE_SHELL } from "./patientPageLayout";
import { PATIENT_THEME } from "./patientTheme";
import { INSURANCE_OPTIONS } from "./patientForm.shared";
import { getPatientCharts } from "../../services/patientService";
import { resolveLatestEpisodePath } from "./resolveLatestEpisodePath";

const canManage = (user) => {
  const code = normalizeRoleCode(resolveUserRoleName(user));
  return code === "ADMIN" || code === "OA";
};

const displayPhoneUS = (value) => {
  if (!value) return "—";
  const formatted = formatPhone(String(value));
  return formatted || value;
};

const Patients = () => {
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const userRole = resolveUserRoleName(currentUser);
  const canEdit = canManage(currentUser);
  const canArchive = canArchivePatient(userRole);
  const [showArchived, setShowArchived] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiveMode, setArchiveMode] = useState("archive");
  const [archiving, setArchiving] = useState(false);

  const {
    patients,
    loading,
    pagination,
    filters,
    statusCounts,
    setFilters,
    setPagination,
    exportPatientsCsv,
    archivePatientById,
    unarchivePatientById,
  } = usePatients({
    enableAdminFilters: canEdit,
    showArchived: canArchive && showArchived,
  });

  const handleViewPatient = useCallback(
    async (patientId) => {
      setOpeningId(patientId);
      try {
        const chartsRes = await getPatientCharts(patientId);
        const payload = chartsRes.result || chartsRes;
        navigate(resolveLatestEpisodePath(patientId, payload));
      } catch (err) {
        toast.error(err.message || "Failed to open patient");
        navigate(`/patients/${patientId}/charts`);
      } finally {
        setOpeningId(null);
      }
    },
    [navigate],
  );

  const openArchiveConfirm = (row, mode) => {
    setArchiveMode(mode);
    setArchiveTarget(row);
  };

  const handleConfirmArchiveAction = useCallback(async () => {
    if (!archiveTarget?.id || archiving) return;
    setArchiving(true);
    try {
      const ok =
        archiveMode === "unarchive"
          ? await unarchivePatientById(archiveTarget.id)
          : await archivePatientById(archiveTarget.id);
      if (ok) setArchiveTarget(null);
    } finally {
      setArchiving(false);
    }
  }, [
    archiveTarget,
    archiveMode,
    archiving,
    archivePatientById,
    unarchivePatientById,
  ]);

  const statusFilters = useMemo(() => {
    const countLabel = (key, label) => {
      const n = statusCounts?.[key];
      return typeof n === "number" ? `${label} (${n})` : label;
    };
    const total = Object.values(statusCounts || {}).reduce(
      (sum, n) => sum + (Number(n) || 0),
      0,
    );
    return [
      {
        value: "",
        label: total ? `All Status (${total})` : "All Status",
      },
      ...PATIENT_STATUS_FILTER_ORDER.map((value) => ({
        value,
        label: countLabel(value, STATUS_LABELS[value]),
      })),
    ];
  }, [statusCounts]);

  const hasActiveFilters = Boolean(
    filters.search || (canEdit && (filters.status || filters.insurance)),
  );

  const clearFilters = () => {
    setFilters((f) => ({
      ...f,
      search: "",
      ...(canEdit ? { status: "", insurance: "" } : {}),
    }));
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const handleSort = (key) => {
    setFilters((f) => ({
      ...f,
      sortBy: key,
      sortOrder: f.sortBy === key && f.sortOrder === "asc" ? "desc" : "asc",
    }));
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportPatientsCsv();
    } finally {
      setExporting(false);
    }
  };

  const cell = "text-[13px] text-slate-700 leading-none";
  const cellMuted = "text-[13px] text-slate-600 leading-none";
  const cellStrong = "text-[13px] font-medium text-slate-900 leading-none";

  const columns = [
    {
      header: "MRN",
      accessor: "mrn",
      sortable: true,
      className: "whitespace-nowrap pl-10 pr-3",
      render: (row) => (
        <span className={cellStrong}>{row.mrn || "—"}</span>
      ),
    },
    {
      header: "Patient Name",
      accessor: "lastName",
      sortable: true,
      className: "min-w-[10rem] max-w-[14rem] px-3",
      render: (row) => {
        const name = [row.lastName, row.firstName]
          .map((p) => String(p || "").trim())
          .filter(Boolean)
          .join(", ");
        return (
          <span className={`${cellStrong} block truncate`} title={name || ""}>
            {name || "—"}
          </span>
        );
      },
    },
    {
      header: "Phone",
      accessor: "phone",
      sortable: true,
      className: "whitespace-nowrap px-3",
      render: (row) => (
        <span className={`${cellMuted} tabular-nums`}>
          {displayPhoneUS(row.phone)}
        </span>
      ),
    },
    {
      header: "City",
      accessor: "city",
      sortable: true,
      className: "whitespace-nowrap max-w-[9rem] px-3",
      render: (row) => (
        <span className={`${cell} block truncate`} title={row.city || ""}>
          {row.city || "—"}
        </span>
      ),
    },
    {
      header: "Insurance",
      accessor: "insurance",
      sortable: true,
      className: "min-w-[8rem] max-w-[12rem] px-3",
      render: (row) => (
        <span
          className={`${cell} block truncate`}
          title={row.insurance || ""}
        >
          {row.insurance || "—"}
        </span>
      ),
    },
    {
      header: "SOC date",
      accessor: "admissionDate",
      sortable: true,
      className: "whitespace-nowrap px-3",
      render: (row) => (
        <span className={`${cellMuted} tabular-nums`}>
          {displayDateUS(row.admissionDate) || "—"}
        </span>
      ),
    },
    {
      header: "Status",
      accessor: "status",
      sortable: true,
      className: "whitespace-nowrap px-3",
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      header: "Actions",
      accessor: "actions",
      className: "whitespace-nowrap pl-3 pr-4",
      render: (row) => (
        <div className="inline-flex items-center gap-1.5">
          {(!showArchived || ARCHIVED_PATIENT_LIST_ACTIONS.showView) ? (
            <button
              type="button"
              onClick={() => handleViewPatient(row.id)}
              disabled={openingId === row.id || archiving}
              className={`inline-flex items-center gap-1 h-6 px-2 text-[11px] font-medium rounded transition-colors disabled:opacity-50 ${PATIENT_THEME.button.soft}`}
            >
              {openingId === row.id ? (
                <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <ViewIcon size={13} />
              )}
              View
            </button>
          ) : null}
          {canArchive && !showArchived ? (
            <button
              type="button"
              onClick={() => openArchiveConfirm(row, "archive")}
              disabled={archiving || openingId === row.id}
              className="inline-flex items-center gap-1 h-6 px-2 text-[11px] font-medium rounded border border-amber-200 bg-white text-amber-800 hover:bg-amber-50 hover:border-amber-300 transition-colors disabled:opacity-50"
              title="Archive patient"
            >
              <ArchiveIcon size={13} />
              Archive
            </button>
          ) : null}
          {canArchive && showArchived && ARCHIVED_PATIENT_LIST_ACTIONS.showUnarchive ? (
            <button
              type="button"
              onClick={() => openArchiveConfirm(row, "unarchive")}
              disabled={archiving}
              className="inline-flex items-center gap-1 h-6 px-2 text-[11px] font-medium rounded border border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-50 hover:border-indigo-300 transition-colors disabled:opacity-50"
              title="Unarchive patient"
            >
              <ArchiveRestoreIcon size={13} />
              Unarchive
            </button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className={PATIENT_PAGE_SHELL}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className={`text-2xl font-bold ${PATIENT_THEME.section.title}`}>
            Patients
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {showArchived
              ? "Archived patients — Admin only. Unarchive to restore access."
              : "Manage patient records, documents, and care team assignments"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {canArchive ? (
            <button
              type="button"
              onClick={() => setShowArchived((v) => !v)}
              className={`inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md transition-colors border ${
                showArchived
                  ? `${PATIENT_THEME.button.primary}`
                  : `${PATIENT_THEME.button.outline}`
              }`}
            >
              {showArchived ? (
                <>
                  <UsersIcon size={15} />
                  Active list
                </>
              ) : (
                <>
                  <ArchiveIcon size={15} />
                  Archived list
                </>
              )}
            </button>
          ) : null}
          {canEdit && (
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className={`inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md transition-colors disabled:opacity-50 border ${PATIENT_THEME.button.outline}`}
            >
              {exporting ? (
                <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <DownloadIcon size={15} />
              )}
              Export CSV
            </button>
          )}
          {canEdit && !showArchived && (
            <button
              type="button"
              onClick={() => navigate("/patients/add")}
              className={`inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-semibold rounded-md transition-colors ${PATIENT_THEME.button.primary}`}
            >
              <PlusIcon size={16} />
              Add Patient
            </button>
          )}
        </div>
      </div>

      <div
        className={`bg-white rounded-xl border shadow-sm overflow-hidden ${PATIENT_THEME.section.wrap}`}
      >
        <div
          className={`flex items-center gap-2 h-11 px-4 border-b box-border ${PATIENT_THEME.section.head}`}
        >
          <span
            className={`w-0.5 h-4 rounded-full shrink-0 ${PATIENT_THEME.section.bar}`}
            aria-hidden
          />
          <span className={`shrink-0 ${PATIENT_THEME.section.icon}`}>
            {showArchived ? <ArchiveIcon size={14} /> : <UsersIcon size={14} />}
          </span>
          <h2
            className={`min-w-0 flex-1 text-xs font-semibold uppercase tracking-wide truncate ${PATIENT_THEME.section.title}`}
          >
            {showArchived ? "Archived list" : "Patient List"}
          </h2>
          {typeof pagination.total === "number" && (
            <span
              className={`inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded text-[10px] font-semibold tabular-nums shrink-0 ${PATIENT_THEME.section.badge}`}
            >
              {pagination.total}
            </span>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 px-3 py-2.5 border-b border-indigo-100">
          <div className="relative flex-1 min-w-0">
            <SearchIcon
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search by name, MRN, phone..."
              className={`w-full h-8 pl-9 pr-8 text-sm bg-white border border-indigo-200 rounded-md text-slate-800 placeholder:text-slate-400 outline-none transition-colors ${PATIENT_THEME.fieldFocus}`}
              value={filters.search}
              onChange={(e) => {
                setFilters((f) => ({ ...f, search: e.target.value }));
                setPagination((p) => ({ ...p, page: 1 }));
              }}
            />
            {filters.search ? (
              <button
                type="button"
                onClick={() => {
                  setFilters((f) => ({ ...f, search: "" }));
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <CloseIcon size={14} />
              </button>
            ) : null}
          </div>

          {canEdit ? (
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <div className="relative">
                <label className="sr-only" htmlFor="patient-status-filter">
                  Filter by status
                </label>
                <select
                  id="patient-status-filter"
                  className={`appearance-none h-8 min-w-[150px] pl-3 pr-8 text-sm rounded-md border outline-none transition-colors cursor-pointer bg-white ${PATIENT_THEME.fieldFocus} ${
                    filters.status
                      ? "border-indigo-400 text-indigo-700 font-medium bg-indigo-50/60"
                      : "border-indigo-200 text-slate-700"
                  }`}
                  value={filters.status}
                  onChange={(e) => {
                    setFilters((f) => ({ ...f, status: e.target.value }));
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                >
                  {statusFilters.map((option) => (
                    <option key={option.value || "all"} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon
                  size={14}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
              </div>

              <div className="relative">
                <label className="sr-only" htmlFor="patient-insurance-filter">
                  Filter by insurance
                </label>
                <select
                  id="patient-insurance-filter"
                  className={`appearance-none h-8 min-w-[160px] pl-3 pr-8 text-sm rounded-md border outline-none transition-colors cursor-pointer bg-white ${PATIENT_THEME.fieldFocus} ${
                    filters.insurance
                      ? "border-indigo-400 text-indigo-700 font-medium bg-indigo-50/60"
                      : "border-indigo-200 text-slate-700"
                  }`}
                  value={filters.insurance}
                  onChange={(e) => {
                    setFilters((f) => ({ ...f, insurance: e.target.value }));
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                >
                  <option value="">All Insurance</option>
                  {INSURANCE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon
                  size={14}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
              </div>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="h-8 px-2.5 text-sm font-medium text-indigo-700 hover:bg-indigo-50 rounded-md border border-indigo-200 transition-colors whitespace-nowrap"
                >
                  Clear
                </button>
              )}
            </div>
          ) : null}
        </div>

        <Table
          columns={columns}
          data={patients}
          loading={loading}
          showSrNo={false}
          density="compact"
          embedded
          headTone={PATIENT_THEME.tableHead}
          sortKey={filters.sortBy}
          sortOrder={filters.sortOrder}
          onSort={handleSort}
          pagination={{
            currentPage: pagination.page,
            totalPages: pagination.totalPages,
            totalItems: pagination.total,
            itemsPerPage: pagination.limit,
            onPageChange: (page) => setPagination((p) => ({ ...p, page })),
          }}
          noDataMessage={
            showArchived
              ? "No archived patients."
              : "No patients found. Add a new patient to get started."
          }
        />
      </div>

      <PatientArchiveConfirmModal
        open={Boolean(archiveTarget)}
        patient={archiveTarget}
        mode={archiveMode}
        loading={archiving}
        onClose={() => {
          if (!archiving) setArchiveTarget(null);
        }}
        onConfirm={handleConfirmArchiveAction}
      />
    </div>
  );
};

export default Patients;
